import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Clock, AlertTriangle, FileText, TrendingUp, 
  CheckCircle, XCircle, LogIn, FileCheck, UserPlus, Calendar
} from 'lucide-react';
import { hrAPI } from '@/services/hr';
import { toast } from '@/components/ui/use-toast';

const HRDashboardPage = () => {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    presentToday: 0,
    pendingOvertime: 0,
    expiringVisas: 0,
    departmentBreakdown: {}
  });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);

  useEffect(() => {
    loadStats();
    loadActivities();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const res = await hrAPI.getDashboardStats();
      
      if (res.success) {
        setStats(res.stats);
      } else {
        toast({ 
          title: 'Error', 
          description: 'Failed to load HR statistics', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('Load error:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to load HR statistics', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    try {
      setLoadingActivities(true);
      const res = await hrAPI.getRecentActivity(10);
      
      if (res.success) {
        setActivities(res.activities);
      }
    } catch (error) {
      console.error('Activity load error:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  // ✅ دالة لتحديد الأيقونة حسب نوع النشاط
  const getActivityIcon = (type, status) => {
    switch(type) {
      case 'overtime':
        return status === 'Approved' 
          ? <CheckCircle className="w-4 h-4 text-green-600" />
          : <XCircle className="w-4 h-4 text-red-600" />;
      case 'attendance':
        return <LogIn className="w-4 h-4 text-blue-600" />;
      case 'daily_report':
        return <FileCheck className="w-4 h-4 text-purple-600" />;
      case 'visa_expiry':
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case 'new_employee':
        return <UserPlus className="w-4 h-4 text-indigo-600" />;
      default:
        return <Clock className="w-4 h-4 text-slate-600" />;
    }
  };

  // ✅ دالة لتحديد لون الخلفية
  const getActivityBgColor = (type, status) => {
    switch(type) {
      case 'overtime':
        return status === 'Approved' ? 'bg-green-50' : 'bg-red-50';
      case 'attendance':
        return 'bg-blue-50';
      case 'daily_report':
        return 'bg-purple-50';
      case 'visa_expiry':
        return 'bg-orange-50';
      case 'new_employee':
        return 'bg-indigo-50';
      default:
        return 'bg-slate-50';
    }
  };

  // ✅ تحويل الوقت لصيغة نسبية
  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <Helmet><title>HR Dashboard - Stone Beam</title></Helmet>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-slate-900">HR Overview</h1>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Employees</p>
                <h3 className="text-3xl font-bold text-slate-900">
                  {loading ? '...' : stats.totalEmployees}
                </h3>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                <Users className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Present Today</p>
                <h3 className="text-3xl font-bold text-slate-900">
                  {loading ? '...' : stats.presentToday}
                </h3>
                <p className="text-xs text-slate-400">
                  Attendance Rate: {stats.totalEmployees ? Math.round((stats.presentToday / stats.totalEmployees) * 100) : 0}%
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                <Clock className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Pending Overtime</p>
                <h3 className="text-3xl font-bold text-slate-900">
                  {loading ? '...' : stats.pendingOvertime}
                </h3>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600">
                <FileText className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Visa Expiring Soon</p>
                <h3 className="text-3xl font-bold text-slate-900">
                  {loading ? '...' : stats.expiringVisas}
                </h3>
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center text-red-600">
                <AlertTriangle className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts / Breakdown */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" /> Staff Distribution by Department
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : Object.keys(stats.departmentBreakdown).length === 0 ? (
                <div className="text-center py-8 text-slate-400">No department data</div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(stats.departmentBreakdown).map(([dept, count]) => (
                    <div key={dept} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-slate-700">{dept}</span>
                        <span className="text-slate-500">{count} Employees</span>
                      </div>
                      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-orange-600 rounded-full" 
                          style={{ width: `${(count / stats.totalEmployees) * 100}%` }} 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ✅ Recent Activity Feed */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" /> Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[400px] overflow-y-auto">
              {loadingActivities ? (
                <div className="text-center py-8 text-slate-500">Loading activities...</div>
              ) : activities.length === 0 ? (
                <div className="text-center py-8 text-slate-400">No recent activity</div>
              ) : (
                <div className="space-y-3">
                  {activities.map((activity, index) => (
                    <div 
                      key={index} 
                      className={`flex items-start gap-3 p-3 rounded-lg border border-slate-100 ${getActivityBgColor(activity.type, activity.status)}`}
                    >
                      <div className="mt-0.5">
                        {getActivityIcon(activity.type, activity.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800 font-medium">
                          {activity.description}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {getRelativeTime(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default HRDashboardPage;
